import { showAlertModal } from "@/utils/utils";

export {
    CALCULATION_REQUIRED_PRODUCT_CODE,
    buildCalculationRequiredRestrictionMessage,
} from "@/utils/calculationRequiredRestriction";

/**
 * @param {Object} product
 * @returns {string|null}
 */
export function resolveProductCode(product) {
    const candidates = [product?.product_code, product?.part_no];
    for (const candidate of candidates) {
        if (candidate == null) {
            continue;
        }
        const normalized = String(candidate).trim();
        if (normalized) {
            return normalized;
        }
    }
    return null;
}

function formatMissingProductsLabel(product) {
    const code = resolveProductCode(product) || product?.product_code;
    if (!code) {
        return "";
    }
    const name = product.product_name || product.name;
    return name && name !== code ? `${code} – ${name}` : code;
}

function normalizeMissingProductsForAlert(missingProducts) {
    const seen = new Set();
    const normalized = [];
    for (const product of missingProducts || []) {
        const code = resolveProductCode(product);
        if (!code || seen.has(code)) {
            continue;
        }
        const label = formatMissingProductsLabel({
            ...product,
            product_code: code,
        });
        if (!label?.trim()) {
            continue;
        }
        seen.add(code);
        normalized.push({
            product_code: code,
            name: product.product_name || product.name || code,
        });
    }
    return normalized;
}

/**
 * @param {Array<{product_code: string, name?: string}>} missingProducts
 * @returns {{ copyableText: string, isProductList: boolean, missingProducts: Array }}
 */
export function buildProductListErrorDetails(missingProducts) {
    const normalized = normalizeMissingProductsForAlert(missingProducts);
    return {
        copyableText: normalized
            .map(product => formatMissingProductsLabel(product))
            .filter(Boolean)
            .join("\n"),
        isProductList: true,
        missingProducts: normalized,
    };
}

export function buildMissingCalculationProductsUploadMessage() {
    return (
        "Atenție! Unele produse necesare pentru calcule și formule lipsesc din lista de prețuri încărcată. " +
        "Ofertele care folosesc „Calculează produse” pot avea rezultate incomplete. " +
        "Verificați următoarele produse:"
    );
}

/**
 * @param {Array} productEntries
 * @param {Array} priceListProducts
 * @returns {Array<{product_code: string, name: string}>}
 */
export function findProductsMissingFromPriceList(productEntries, priceListProducts) {
    const priceListCodes = new Set(
        (priceListProducts || [])
            .map(product => resolveProductCode(product))
            .filter(Boolean)
    );
    const seen = new Set();
    const missing = [];
    for (const product of productEntries || []) {
        const code = resolveProductCode(product);
        if (!code || seen.has(code) || priceListCodes.has(code)) {
            continue;
        }
        seen.add(code);
        missing.push({
            product_code: code,
            name: product.product_name || product.name || code,
        });
    }
    return missing;
}

/** @type {Promise<Map<string, {product_code: string, name: string, catalogs: string[]}>>|null} */
let calculationRequiredLookupPromise = null;

/**
 * Lazy map of product_code → { name, catalogs } for offer-calculation catalogs.
 * @returns {Promise<Map<string, {product_code: string, name: string, catalogs: string[]}>>}
 */
async function loadCalculationRequiredLookup() {
    if (!calculationRequiredLookupPromise) {
        calculationRequiredLookupPromise = (async () => {
            const [
                { ceilingProducts },
                { ipa_products },
                { products: vmcProducts },
                { pdcProducts },
                { products: vacuumProducts },
                { plumbingProducts },
            ] = await Promise.all([
                import("@/CEILING/ceilingProducts.js"),
                import("@/IPA/IPAProducts.js"),
                import("@/VMC/vmcProducts.js"),
                import("@/PDC/pdcProducts.js"),
                import("@/Vacuum/VacuumProducts.js"),
                import("@/PLUMBING/plumbingProducts.js"),
            ]);

            const catalogBundles = [
                { label: 'Tavan', products: ceilingProducts },
                { label: 'IPA', products: ipa_products },
                { label: 'VMC', products: vmcProducts },
                { label: 'PDC', products: pdcProducts },
                { label: 'Aspirare', products: vacuumProducts },
                { label: 'Sanitare', products: plumbingProducts },
            ];

            const byCode = new Map();
            for (const bundle of catalogBundles) {
                for (const product of bundle.products || []) {
                    const code = resolveProductCode(product);
                    if (!code) {
                        continue;
                    }
                    const existing = byCode.get(code);
                    if (existing) {
                        if (!existing.catalogs.includes(bundle.label)) {
                            existing.catalogs.push(bundle.label);
                        }
                        continue;
                    }
                    byCode.set(code, {
                        product_code: code,
                        name: product.name || product.product_name || code,
                        catalogs: [bundle.label],
                    });
                }
            }
            return byCode;
        })();
    }
    return calculationRequiredLookupPromise;
}

/**
 * Product codes from offer catalogs that use „Calculează produse”.
 * Catalogs are loaded on demand to avoid bloating the price-list page bundle.
 * @param {string} priceListType
 * @returns {Promise<Array<{product_code: string, name: string}>>}
 */
export async function loadCalculationRequiredProducts(priceListType) {
    if (priceListType !== "general") {
        return [];
    }

    const lookup = await loadCalculationRequiredLookup();
    return Array.from(lookup.values()).map(({ product_code, name }) => ({
        product_code,
        name,
    }));
}

/**
 * If the product is used by offer „Calculează produse” catalogs, return its info.
 * @param {string|null|undefined} productCode
 * @returns {Promise<{product_code: string, name: string, catalogs: string[]}|null>}
 */
export async function findCalculationRequiredProduct(productCode) {
    const code = String(productCode || '').trim();
    if (!code) {
        return null;
    }
    const lookup = await loadCalculationRequiredLookup();
    return lookup.get(code) || null;
}

/**
 * @param {string} priceListType
 * @param {Array} priceListProducts
 * @returns {Promise<Array<{product_code: string, name: string}>>}
 */
export async function findCalculationProductsMissingFromUploadedPriceList(
    priceListType,
    priceListProducts
) {
    const required = await loadCalculationRequiredProducts(priceListType);
    return findProductsMissingFromPriceList(required, priceListProducts);
}

/**
 * @param {Object} store - Vuex store
 * @param {Array<{product_code: string, name?: string}>} missingProducts
 */
export function showMissingCalculationProductsUploadAlert(store, missingProducts) {
    const errorDetails = buildProductListErrorDetails(missingProducts);
    if (!errorDetails.copyableText?.trim()) {
        return;
    }
    showAlertModal(
        store,
        buildMissingCalculationProductsUploadMessage(),
        "warning",
        15000,
        errorDetails
    );
}
